/**
 * Author: Harsh Agrawal
 * Summary: This page validates user emails. For new users, it redirects them to the new checkout page; for existing users, it redirects to the upgrade checkout page. Additionally, it parses URL search parameters including items, quantity, email, promoCode, and token. Depending on the provided items, users might be redirected to a non-eligible page.
 */



import React from 'react'
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Header from '../../components/layout/header'
import Footer from '../../components/layout/footer'
import OrderSummary from '../../components/orderSummary';
import { validateUserEmail } from '../../reducers/auth';
import { addFormCustomFieldError, validateForm } from '../../utils/form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setCart, updateDisplayPurchaseComplete } from '../../reducers/cart';
import { createItemPayload, parseItemsFromURLSearchParams, redirectUserToBaseCurrencyURL } from '../../utils/cart';
import { focusAndOpenKeyboard } from '../../utils/globalUtils';
import { initChargebee } from '../../reducers/chargebee';
import { fetchEstimateForNewSubscription } from '../../reducers/cart';
import { displayError } from '../../reducers/snackbar';
import { logout } from '../../reducers/auth';
import { LinearProgress } from '@mui/material';
import countryList from 'react-select-country-list';
import { addGdprUpdate } from '../../reducers/accountDetails';
import localStore from '../../utils/localstore';
import { getTranslation } from '../../reducers/translation';
import {
    TRANSLATION_TYPE,
    UNITED_KINGDOM_COUNTRY_CODE,
    resolvePayPalCurrencies
} from '../../utils/constants';
import { vvLogin2 } from '../../settings.js/dev';
import { getBaseUrl } from '../../utils/functions';
import { getInterpolatedString } from '../../utils/text';
import { setPaymentModuleSettings } from '../../reducers/chargebee';
import { parseURLParams } from '../../reducers/sessionDetails';

export default function EmailValidation() {
    const [email, setEmail] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const [displayPage, setdisplayPage] = useState(false);
    const { countryCode } = useSelector((state) => state.auth);
    const [country, setCountry] = useState(countryCode);
    const { gdprEmailUpdates } = useSelector((state) => state.accountDetails);
    const [baseCurrencyItems, setBaseCurrencyItems] = useState([]);
    const [cbInit, setCbInit] = useState({});
    const [validateResponse, setValidateResponse] = useState({});
    const [ukAddressBlocked, setUkAddressBlocked] = useState(false);

    useEffect(() => {
        setCountry(countryCode);
    }, [countryCode])

    // Keep the UK block in sync with the selected country, including on initial load
    // and when the geo-detected country arrives asynchronously.
    useEffect(() => {
        validateUKAndBlock(country);
    }, [country])


    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { items, count, estimate, promoCode } = useSelector((state) => state.cart)
    const { isCbInitiated, cbInstance, paymentModule, isApplePaySupported, payPalCurrencies } = useSelector((state) => state.chargebee);
    const t = useSelector(getTranslation)
    const t_label = t[TRANSLATION_TYPE.LABEL] ?? {}
    const t_message = t[TRANSLATION_TYPE.MESSAGE] ?? {}

    const options = useMemo(() => countryList().getData(), [])
    const gdprCountry = ["Austria", "Belgium", "Bulgaria", "Croatia", "Cyprus", "Czechia", "Denmark", "Estonia", "Finland", "France", "Germany", "Greece", "Hungary", "Ireland", "Italy", "Latvia", "Lithuania", "Luxembourg", "Malta", "Netherlands", "Poland", "Portugal", "Romania", "Slovakia", "Slovenia", "Spain", "Sweden", "Canada"];

    useEffect(() => {
        dispatch(logout());
        dispatch(parseURLParams(searchParams));
        focusAndOpenKeyboard(document.getElementById('email'), 300);
        // initializeCart(searchParams);
        dispatch(updateDisplayPurchaseComplete({ 'displayPurchaseComplete': false }))
        // if (!isCbInitiated) {
        dispatch(initChargebee()).then((response) => {
            setCbInit(response);
            initializeCart(searchParams, response);
        }).catch((error) => {
            dispatch(displayError(error));
        })
        // } else
        //     initializeCart(searchParams, { isApplePaySupported });
    }, [])


    const initializeCart = (searchParams, cbInit) => {
        let { cart, count, promoCode, userEmail, token } = parseItemsFromURLSearchParams(searchParams);
        dispatch(setCart({ count, "items": cart, promoCode }));

        // Kick off the estimate once, up front, and keep the in-flight promise. redirectUser reuses this
        // same request to learn the checkout currency, so we neither fire a duplicate estimate call nor
        // let a second response overwrite valid estimate state.
        let estimatePayload = { items: createItemPayload(cart, count) };
        if (promoCode)
            estimatePayload = { ...estimatePayload, promoCode };
        if (userEmail)
            estimatePayload = { ...estimatePayload, email: userEmail };
        const estimatePromise = dispatch(fetchEstimateForNewSubscription(estimatePayload));
        estimatePromise.catch((error) => {
            setTimeout(() => {
                dispatch(displayError(error));
            }, 200);
        });

        if (userEmail || token) {

            setEmail(userEmail);
            if (token)
                localStorage.setItem('token', token);

            const emailPresent = userEmail != null && userEmail !== '';

            setTimeout(() => {
                let payload = { email: userEmail, items: createItemPayload(cart, count) }
                dispatch(validateUserEmail(payload))
                    .then((response) => { redirectUser(response, !emailPresent, cbInit, { items: cart, count, promoCode, email: userEmail, estimatePromise }) })
                    .catch((error) => {
                        setdisplayPage(true);
                        setTimeout(() => {
                            validateForm("email_validation_form");
                        }, 10);
                        if (token)
                            localStorage.removeItem('token');
                        dispatch(displayError(error));
                    });
            }, 200);
        } else {
            setdisplayPage(true);
        }
    }

    const updateEmail = (e) => {
        setEmail(e.target.value);
    }

    const handleSwitchCurrency = (e) => {
        redirectUserToBaseCurrencyURL(baseCurrencyItems?.items);
    }

    const displayBaseCurrencyModal = (items, baseCurrency) => {
        document.body.style.overflow = 'hidden';
        document.getElementById('switch-currency').classList.add('active');
        setBaseCurrencyItems({ items, baseCurrency });
    }

    const displayIneligibleTrialModal = () => {
        document.body.style.overflow = 'hidden';
        document.getElementById('trial-ineligible').classList.add('active');
    }

    const closeIneligibleTrialModal = () => {
        document.body.style.overflow = 'auto';
        document.getElementById('trial-ineligible').classList.remove('active');
    };


    // We are not permitted to sell to United Kingdom addresses. Block when the country is UK, clear otherwise.
    // Returns true when the country is allowed (not UK), false when it is blocked.
    const validateUKAndBlock = (country) => {
        const isUK = country === UNITED_KINGDOM_COUNTRY_CODE;
        setUkAddressBlocked(isUK);
        return !isUK;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // We are not permitted to sell to United Kingdom addresses, so block progression here.
        if (validateUKAndBlock(country) === false) {
            return;
        }
        validateAndSubmit(email, items, count, true)
            .then((response) => {
                redirectUser(response, false, cbInit, { items, count, promoCode, email })
            })
            .catch((error) => dispatch(displayError(error)));
    }

    const validateAndSubmit = async (email, items, count, validateForm_) => {
        if (validateForm_ && !validateForm("email_validation_form"))
            return;

        let payload = { email, items: createItemPayload(items, count) }
        return dispatch(validateUserEmail(payload));
    }

    const signInOption = (loginLink) => {
        let label_ = t_label['checkout.label.existing_user_sign_in'] ?? "User already exists, please <a href={loginLink} class='text-blue-600 underline hover:text-blue-700 cursor-pointer'>Sign In</a>";
        return `<div>${getInterpolatedString(label_, { 'loginLink': loginLink })}</div>`
    }

    const redirectUser = async (response, skipLogin, cbInit, ctx = {}) => {
        let { userStatus, status, description, message, isBaseCurrencyDifferent, username, showTrialWithInYearNotApplicable, navigateTo } = response;
        setValidateResponse({ ...response, skipLogin });

        if (isBaseCurrencyDifferent) {
            setdisplayPage(true);
            setTimeout(() => {
                displayBaseCurrencyModal(response?.items, response?.baseCurrency);
            }, 200);
            return;
        }

        const paypalInModule = cbInit?.paymentModule?.includes('paypal_express_checkout') || paymentModule?.includes('paypal_express_checkout');
        // PayPal is currency-gated (EUR only), so we must know the checkout currency BEFORE deciding
        // whether to show the wallet/options page — otherwise a USD checkout wrongly lands on it.
        // Resolve it without trusting captured/stale state: reuse the in-flight estimate handed in by the
        // caller, else the estimate already in state, else a fresh fetch built from the caller's context.
        let currencyCode;
        if (ctx.estimatePromise) {
            try { currencyCode = (await ctx.estimatePromise)?.currencyCode; } catch (e) { /* fall through */ }
        }
        if (!currencyCode) currencyCode = estimate?.currencyCode;
        if (!currencyCode) {
            try {
                let estPayload = { items: createItemPayload(ctx.items ?? items, ctx.count ?? count) };
                const promoCode_ = ctx.promoCode ?? promoCode;
                const email_ = ctx.email ?? email;
                if (promoCode_) estPayload = { ...estPayload, promoCode: promoCode_ };
                if (email_) estPayload = { ...estPayload, email: email_ };
                currencyCode = (await dispatch(fetchEstimateForNewSubscription(estPayload)))?.currencyCode;
            } catch (e) {
                // Couldn't determine currency — fall through to the card flow (no wallet options page).
            }
        }
        const paypalCurrencyEligible = !!currencyCode && resolvePayPalCurrencies(cbInit?.payPalCurrencies ?? payPalCurrencies).includes(currencyCode);
        let displayPaymentOptionPage = cbInit?.isApplePaySupported === true || isApplePaySupported || (paypalInModule && paypalCurrencyEligible);

        if (userStatus === "EXIST") {
            if (status === "success") {
                if (showTrialWithInYearNotApplicable) {
                    displayIneligibleTrialModal();
                } else if (response?.hasActiveSubscription === false) {
                    // Returning customer whose previous subscription is cancelled/expired — treat them like a new customer for payment method selection
                    if (skipLogin) {
                        if (displayPaymentOptionPage)
                            navigate("/checkout/choosePaymentMethod");
                        else
                            navigate('/checkout/addbillingdetails')
                    } else {
                        displayLoginOption(displayPaymentOptionPage ? "/checkout/choosePaymentMethod" : "/checkout/addbillingdetails", username)
                    }
                } else {
                    redirectExistingUser(skipLogin, username);
                }
            } else if (status === "error")
                navigate(navigateTo || "/checkout/noteligible", { state: { message, description } })
        }
        else if (userStatus === "NEW") {
            if (status === "error")
                navigate(navigateTo || "/checkout/noteligible", { state: { message, description } })
            else {
                if (displayPaymentOptionPage)
                    navigate("/checkout/choosePaymentMethod");
                else
                    navigate('/checkout/addbillingdetails')
            }
        } else if (userStatus === "EXIST_NO_SUB") {
            if (status === "success") {
                if (skipLogin) {
                    if (displayPaymentOptionPage)
                        navigate("/checkout/choosePaymentMethod");
                    else
                        navigate('/checkout/addbillingdetails')
                }
                else {
                    displayLoginOption(displayPaymentOptionPage ? "/checkout/choosePaymentMethod" : "/checkout/addbillingdetails", username)
                }
            } else if (status === "error")
                navigate(navigateTo || "/checkout/noteligible", { state: { message, description } })
        }
        setdisplayPage(true);
    }

    const redirectExistingUser = (skipLogin, username) => {
        if (skipLogin) {
            navigate("/checkout/viewbillingdetails");
        }
        else {
            displayLoginOption("/checkout/viewbillingdetails", username)
        }
    }

    const displayLoginOption = (redirectUrlPath, username) => {
        var redirectUrl = vvLogin2 + getBaseUrl() + redirectUrlPath;
        if (username != null)
            redirectUrl += `&email=${username}`;
        addFormCustomFieldError("email_validation_form", document.getElementById("email"), signInOption(redirectUrl));
        // navigate('/checkout/login')
    }

    const isGDPRCountry = () => {
        let gdpr_country = options.find((option_ => option_.value === country));
        var value = gdprCountry.includes(gdpr_country?.label);
        if (value)
            dispatch(addGdprUpdate({ gdprEmailUpdates: gdprEmailUpdates ?? false }))
        else
            dispatch(addGdprUpdate({ gdprEmailUpdates: undefined }))
        return value;
    }

    const updateGDPRCheckbox = (e) => {
        dispatch(addGdprUpdate({ gdprEmailUpdates: e.target.checked }))
    }

    const handleContinueWithSubscription = () => {
        closeIneligibleTrialModal();
        redirectExistingUser(validateResponse?.skipLogin, validateResponse?.username)
    }

    return (
        <>
            {
                !displayPage &&
                <LinearProgress />
            }

            <>
                <div className={displayPage ? 'body body--alt2' : 'hidden'}>
                    <Header />

                    <main className="main main--fullh">
                        <section className="section personal-details">
                            <div className="grid-cont">
                                <form onSubmit={handleSubmit} noValidate id="email_validation_form" className="personal-details__form">
                                    <h2 className="personal-details__heading mb-8">{t_label?.['checkout.label.tell_us_your_email'] ?? 'Tell us your email'}</h2>

                                    <label className="personal-details__label">{t_label?.['checkout.label.email'] ?? 'Email'}</label>
                                    <div className="form-group personal-details__control personal-details__control--hastip">
                                        <input required disabled="" type="email" value={email} name="email" id="email" className="personal-details__input" onChange={updateEmail} data-pristine-maxLength="70" data-pristine-maxlength-message={t_message?.['checkout.message.email_min_length'] ?? "Email cannot have more than 70 characters"} data-pristine-required-message={t_message?.['checkout.message.required'] ?? 'This field is required'} data-pristine-email-message={t_message?.['checkout.message.valid_email'] ?? 'This field requires a valid e-mail address'} />
                                    </div>

                                    <div className={`form-group personal-details__control ${ukAddressBlocked ? 'has-danger' : ''}`}>
                                        <label htmlFor="country" className="personal-details__label">{t_label?.['checkout.label.country'] ?? 'Country'}</label>

                                        <select required name="country" id="country" defaultValue={country} value={country} className="personal-details__select"
                                            onChange={(e) => setCountry(e.target.value)}
                                        >
                                            {options.map(option_ =>
                                                <option key={option_.value} value={option_.value}>{option_.label}</option>
                                            )}
                                        </select>

                                        {
                                            ukAddressBlocked &&
                                            <div className="pristine-error text-help">{t_message?.['checkout.message.uk_not_supported'] ?? 'We are unable to sell to addresses in the United Kingdom.'}</div>
                                        }

                                    </div>

                                    {
                                        isGDPRCountry() &&
                                        <label class="text-md form-group personal-details__checkbox">
                                            <input type="checkbox" id="gdpr" name="gdpr" data-pristine-required-message={t_message?.['checkout.message.mandatory_checkbox'] ?? 'Please check this box to proceed'} onChange={updateGDPRCheckbox} />
                                            <span>{t_message?.['checkout.message.marketing_email'] ?? 'Sign up to receive VectorVest updates and marketing via email. You may unsubscribe at any time.'}</span>
                                        </label>
                                    }

                                    <button type="submit" className="button personal-details__button">{t_label?.['checkout.button.continue'] ?? 'Continue'}</button>

                                </form>
                                <OrderSummary />
                            </div>
                        </section>
                    </main>

                    <Footer />
                </div>


                <section id="switch-currency" className="modal">
                    <div className="modal__back"></div>
                    <button type="button" className="modal__close"></button>
                    <div className="modal__frame">
                        <div className='form-header'>
                            <h4>Switch Currency</h4>
                        </div>

                        <div className="modal__cont">
                            <div>{`Your account is set up to pay in ${baseCurrencyItems?.baseCurrency}`}</div>
                            <p>{`Click the button to be redirected to our ${baseCurrencyItems?.baseCurrency} site`}</p>
                        </div>

                        <div className='form-footer'>
                            <div onClick={handleSwitchCurrency} className='form-footer__action_button bg-2'>
                                {`LOAD ${baseCurrencyItems?.baseCurrency} SITE`}
                            </div>
                        </div>
                    </div>
                </section>

                <section id="trial-ineligible" className="modal">
                    <div className="modal__back"></div>
                    <button type="button" className="modal__close"></button>
                    <div className="modal__frame">
                        <div className='form-header'>
                            <h4>Trial Ineligible</h4>
                        </div>

                        <div className="modal__cont">
                            <div>You are not eligible for a trial as our records indicate that you have had a trial subscription in the past year. Please click "Continue with Subscription" below to sign up a full subscription. If you have any questions, please <a target="_blank" href='https://www.vectorvest.com/contact-us/' className='cursor-pointer' style={{ 'color': '#4976ff', textDecoration: 'none' }}>contact our support team.</a></div>
                        </div>

                        <div className='form-footer'>
                            <div onClick={handleContinueWithSubscription} className='form-footer__action_button bg-2'>
                                Continue with Subscription
                            </div>
                        </div>
                    </div>
                </section>

            </>
        </>
    )
}